import { useMutation, useQuery } from "react-query";
import { client } from "./axios";
import { config } from "../config";
import {
  ApiBasePathParams,
  ApiError,
  FetchPaymentRecordsRequestParams,
  Omnitoken,
  OmnitokenInfo,
  PaymentsInfo,
  QueryOptions,
} from "../types";
import { DEFAULT_QUERY_OPTIONS } from "../constants";
import { PaymentStatus, TransactionType } from "../enums";
import { AxiosError } from "axios";

const buildPaymentsUrl = (
  pathTemplate: string,
  paymentsRequestParams: FetchPaymentRecordsRequestParams,
): string => {
  const {
    orgId,
    accountId,
    invoiceNumber,
    paymentStatus,
    transactionType,
    filters,
  } = paymentsRequestParams;

  let path = pathTemplate
    .replace("{orgId}", orgId.toString())
    .replace("{accountId}", accountId.toString())
    .replace("{invoiceNumber}", invoiceNumber.toString());

  if (paymentStatus === PaymentStatus.All) {
    path += `?status=${PaymentStatus.TransactionInitiated}&status=${PaymentStatus.PaymentSuccess}&status=${PaymentStatus.PaymentFailed}&status=${PaymentStatus.ReturnSuccess}&status=${PaymentStatus.ReturnFailed}`;
  } else {
    path += `?status=${paymentStatus}`;
  }

  if (transactionType === TransactionType.All) {
    path += `&transactionType=${TransactionType.Sale}&transactionType=${TransactionType.Refund}`;
  } else {
    path += `&transactionType=${transactionType}`;
  }

  const queryParams = new URLSearchParams(
    Object.entries(filters).reduce(
      (params, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params[key] = value.toString();
        }
        return params;
      },
      {} as Record<string, string>,
    ),
  );

  if (queryParams.toString()) {
    path += `&${queryParams.toString()}`;
  }

  return path;
};

export const useFetchPaymentRecordsQuery = (
  paymentsRequestParams: FetchPaymentRecordsRequestParams,
  queryOptions?: QueryOptions<any[]>,
) => {
  const getAllPaymentRecords = async (): Promise<PaymentsInfo> => {
    const path = buildPaymentsUrl(
      config.api.paymentService.getAllPaymentRecords,
      paymentsRequestParams,
    );
    const response = await client.get<any>(path);
    return response.data.payments;
  };
  return useQuery(
    ["getAllPaymentRecords", paymentsRequestParams],
    getAllPaymentRecords,
    {
      ...queryOptions,
      ...DEFAULT_QUERY_OPTIONS,
    },
  );
};

export const useFetchDownloadPaymentsCsvQuery = (
  paymentsRequestParams: FetchPaymentRecordsRequestParams,
  queryOptions?: QueryOptions<string>,
) => {
  const downloadPayments = async (): Promise<string> => {
    const path = buildPaymentsUrl(
      config.api.paymentService.downloadPaymentsCsv,
      paymentsRequestParams,
    );
    const response = await client.get<any>(path, {
      headers: {
        "Content-Type": "text/csv",
        Accept: "text/csv",
      },
    });

    return response.data;
  };
  return useQuery(["downloadPayments"], downloadPayments, {
    ...queryOptions,
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useFetchPreviewPaymentReceiptQuery = (
  orgId: number,
  accountId: number,
  invoiceNumber: string,
  referenceNumber: string,
  isRefund: boolean,
  queryOptions?: QueryOptions<string>,
) => {
  const fetchPreviewPaymentReceipt = async (): Promise<ArrayBuffer> => {
    const path =
      config.api.paymentService.previewPaymentReceipt
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{invoiceNumber}", invoiceNumber.toString())
        .replace("{referenceNumber}", referenceNumber.toString()) +
      `${isRefund ? "?refund=true" : ""}`;
    const response = await client.get<any>(path, {
      headers: {
        "Content-Type": "application/pdf",
        Accept: "application/pdf",
      },
      responseType: "arraybuffer",
      responseEncoding: "binary",
    });
    return response.data;
  };

  return useQuery(
    [
      "fetchPreviewPaymentReceipt",
      orgId,
      accountId,
      invoiceNumber,
      referenceNumber,
    ],
    fetchPreviewPaymentReceipt,
    {
      ...queryOptions,
      ...DEFAULT_QUERY_OPTIONS,
    },
  );
};

export const useFetchOmnitokensQuery = (
  orgId: number,
  accountId: number,
  queryOptions?: QueryOptions<string>,
) => {
  const fetchOmnitokens = async (): Promise<OmnitokenInfo> => {
    const path = config.api.paymentService.getOmnitokens
      .replace("{orgId}", orgId.toString())
      .replace("{accountId}", accountId.toString());

    const response = await client.get<any>(path);

    return response.data;
  };

  return useQuery(["fetchOmnitokens", orgId, accountId], fetchOmnitokens, {
    ...queryOptions,
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useDeleteOmnitokenMutation = () => {
  const mutation = useMutation<any, AxiosError<ApiError>, any, any>(
    async (data: ApiBasePathParams & { id: string }) => {
      const { orgId, accountId, id } = data;

      const path = config.api.paymentService.deleteOmnitoken
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{id}", id.toString());

      const response = await client.delete(path);

      return response;
    },
  );

  return mutation;
};

export const useUpdateOmnitokenMutation = () => {
  const mutation = useMutation<any, AxiosError<ApiError>, any, any>(
    async (
      data: ApiBasePathParams & { id: string } & Pick<Omnitoken, "cardLabel">,
    ) => {
      const { orgId, accountId, id, cardLabel } = data;

      const path = config.api.paymentService.updateOmnitoken
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{id}", id.toString());

      const response = await client.patch(path, { cardLabel });

      return response;
    },
  );

  return mutation;
};
